import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import SEOHead from '../components/SEOHead';
import { FREE_PM_CHECKLISTS_URL, PUBLIC_PLANS, SALES_MAILTO, SUPPORT_EMAIL, displayPlan, publicBadge, replaceUnverified } from '../lib/publicSite';

const API = process.env.REACT_APP_BACKEND_URL;
import { 
  CheckCircle2, 
  ArrowRight, 
  ClipboardList, 
  Users, 
  Package, 
  Clock, 
  BarChart3, 
  Shield,
  Zap,
  Globe,
  MessageSquare,
  ChevronDown,
  Menu,
  X
} from 'lucide-react';

const LandingPage = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);

  // Static fallback data - defined BEFORE being used
  const features = [
    {
      icon: <ClipboardList className="w-8 h-8" />,
      title: "Work Order Management",
      description: "Create, assign, and track maintenance tasks in real-time with priority-based workflows."
    },
    {
      icon: <Package className="w-8 h-8" />,
      title: "Inventory Tracking",
      description: "Monitor parts and supplies with automatic low-stock alerts and usage tracking."
    },
    {
      icon: <Clock className="w-8 h-8" />,
      title: "Time & Cost Tracking",
      description: "Log labor hours and calculate costs automatically for accurate reporting."
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: "Team Collaboration",
      description: "Role-based access for admins, supervisors, and technicians with real-time updates."
    },
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Advanced Analytics",
      description: "Visualize performance metrics and generate comprehensive maintenance reports."
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: "Multi-Location Support",
      description: "Manage multiple buildings, floors, and locations from a single dashboard."
    }
  ];

  const benefits = [
    "Create and assign work orders",
    "Schedule preventive maintenance",
    "Track parts and reorder points",
    "Keep each company's data separate",
    "See open and completed work",
    "Start on the free plan"
  ];

  const featureIcons = {
    ClipboardList,
    Package,
    Clock,
    Users,
    BarChart3,
    Globe,
    Shield,
    Zap,
  };

  const renderFeatureIcon = (icon) => {
    if (icon && typeof icon !== 'string') return icon;
    const Icon = featureIcons[icon] || ClipboardList;
    return <Icon className="w-8 h-8" />;
  };

  const faqs = [
    {
      question: "What does checkout actually charge?",
      answer: `The Free plan is $0, with up to 3 users and 100 work orders total. Pro is $29 per month flat for the company, with unlimited users and work orders. Enterprise is contact us at ${SUPPORT_EMAIL}.`
    },
    {
      question: "How is the free plan different from the trial?",
      answer: `A new company starts a 14-day trial: up to 5 users and 50 work orders, and no card. The Free plan is $0 with up to 3 users and 100 work orders total. A trial does not switch to Free by itself. Email ${SUPPORT_EMAIL} to switch to Free.`
    },
    {
      question: "Is my data separate?",
      answer: "Each company's records are stored separately. Sign-in uses an httpOnly cookie. The privacy, terms, and cookie pages are drafts pending legal review."
    },
    {
      question: "How do I reach sales or support?",
      answer: `Email ${SUPPORT_EMAIL}.`
    }
  ];

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await fetch(`${API}/api/landing-page/content`);
        const data = await response.json();
        setContent(data);
      } catch (error) {
        console.error('Failed to fetch landing content:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchContent();
  }, []);

  // Use dynamic content if available, otherwise use hardcoded defaults
  const heroContent = {
    badge: "Work orders, preventive maintenance, and inventory",
    title: "Maintenance management for real facilities",
    subtitle: "Track work orders, schedule preventive maintenance, and watch parts inventory from one workspace. Start on the Free plan or open the shared demo.",
    primaryButton: "Get Started",
    secondaryButton: "Try Demo Account",
    ...(content?.hero || {}),
  };
  heroContent.badge = publicBadge(heroContent.badge);
  heroContent.subtitle = replaceUnverified(
    heroContent.subtitle,
    'Track work orders, schedule preventive maintenance, and watch parts inventory from one workspace. Start on the Free plan or open the shared demo.'
  );
  const dynamicFeatures = content?.features && content.features.length > 0 ? content.features : features;
  const dynamicPricing = PUBLIC_PLANS.map(displayPlan);
  const dynamicFaqs = content?.faqs && content.faqs.length > 0 ? content.faqs : faqs;

  return (
    <>
      {/* SEO Meta Tags and Structured Data */}
      <SEOHead />
      
      <div className="bg-white min-h-screen">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm border-b border-gray-200 z-50" role="navigation" aria-label="Main navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h2 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">
                WorkStream Pro
              </h2>
            </div>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-gray-700 hover:text-blue-600 transition" aria-label="View features">Features</a>
              <a href="#pricing" className="text-gray-700 hover:text-blue-600 transition" aria-label="View pricing">Pricing</a>
              <a href="/pricing" className="text-gray-700 hover:text-blue-600 transition" aria-label="View pricing page">Pricing page</a>
              <a href="#faq" className="text-gray-700 hover:text-blue-600 transition" aria-label="Frequently asked questions">FAQ</a>
              <Button 
                onClick={() => navigate('/login')}
                variant="ghost"
                className="text-gray-700 hover:text-blue-600"
                aria-label="Sign in to your account"
              >
                Sign In
              </Button>
              <Button 
                onClick={() => navigate('/get-started')}
                className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white"
              >
                Get Started Free
              </Button>
            </div>

            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden p-2"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-200">
            <div className="px-4 py-3 space-y-3">
              <a href="#features" className="block text-gray-700 hover:text-blue-600">Features</a>
              <a href="#pricing" className="block text-gray-700 hover:text-blue-600">Pricing</a>
              <a href="/pricing" className="block text-gray-700 hover:text-blue-600">Pricing page</a>
              <a href="#faq" className="block text-gray-700 hover:text-blue-600">FAQ</a>
              <Button 
                onClick={() => navigate('/login')}
                variant="outline"
                className="w-full"
              >
                Sign In
              </Button>
              <Button 
                onClick={() => navigate('/get-started')}
                className="w-full bg-gradient-to-r from-blue-600 to-cyan-500 text-white"
              >
                Get Started Free
              </Button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <header className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 via-white to-cyan-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center max-w-4xl mx-auto">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-100 rounded-full text-blue-700 text-sm font-medium mb-6" role="note" aria-label="Social proof">
              <Zap className="w-4 h-4" aria-hidden="true" />
              {heroContent.badge}
            </div>
            
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight">
              {heroContent.title.split(' ').slice(0, 2).join(' ')}
              <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent"> {heroContent.title.split(' ').slice(2).join(' ')}</span>
            </h1>
            
            <p className="text-xl text-gray-600 mb-10 leading-relaxed">
              {heroContent.subtitle}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12">
              <Button 
                onClick={() => navigate('/get-started')}
                size="lg"
                className="bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white text-lg px-8 py-6 rounded-xl shadow-lg hover:shadow-xl transition-all"
                aria-label="Start free trial of WorkStream Pro"
              >
                {heroContent.primaryButton}
                <ArrowRight className="ml-2 w-5 h-5" aria-hidden="true" />
              </Button>
              <Button 
                onClick={() => navigate('/login?demo=true')}
                data-testid="hero-demo-cta"
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6 rounded-xl border-2 border-gray-800 text-gray-800 hover:border-blue-600 hover:text-blue-600 hover:bg-blue-50 transition-all shadow-md"
                aria-label="Try demo account with pre-loaded data"
              >
                {heroContent.secondaryButton}
              </Button>
            </div>

            <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-500" aria-hidden="true" />
                Free plan: $0
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-500" aria-hidden="true" />
                No credit card required
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-green-500" aria-hidden="true" />
                14-day trial (5 users, 50 work orders)
              </div>
            </div>
          </div>
        </div>
      </header>

      <section className="py-12 bg-white border-y border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-gray-500 mb-8 text-sm uppercase tracking-wide">What the workspace includes</p>
          <div className="grid md:grid-cols-3 gap-6">
            {[
              ['Work orders', 'Create, assign, and close jobs with status, notes, and photos.'],
              ['Locations', 'Organize buildings, floors, and places so every job has a location.'],
              ['Inventory', 'Track quantity on hand and a reorder point for each part.'],
            ].map(([title, copy]) => (
              <div key={title} className="border border-gray-200 rounded-xl p-6">
                <h3 className="font-semibold text-gray-900 mb-2">{title}</h3>
                <p className="text-gray-600 text-sm">{copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Everything You Need to Manage Maintenance
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Powerful features designed to streamline your operations and keep your facilities running smoothly.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {dynamicFeatures.map((feature, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-blue-200 group"
              >
                <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-cyan-100 rounded-xl flex items-center justify-center text-blue-600 mb-6 group-hover:scale-110 transition-transform">
                  {renderFeatureIcon(feature.icon)}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-600 to-cyan-500 text-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl sm:text-5xl font-bold mb-6">
                Transform Your Maintenance Operations
              </h2>
              <p className="text-xl text-blue-100 mb-8 leading-relaxed">
                One workspace for work orders, preventive maintenance, inventory, and locations.
              </p>
              <div className="grid sm:grid-cols-2 gap-4">
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle2 className="w-6 h-6 text-cyan-300 flex-shrink-0" />
                    <span className="text-lg">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 border border-white/20">
              <div className="space-y-6">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Shield className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg">Separate companies</h4>
                    <p className="text-blue-100">Each company sees only its own records</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Zap className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg">Lightning Fast</h4>
                    <p className="text-blue-100">Real-time updates across all devices</p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Globe className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="font-bold text-lg">Access Anywhere</h4>
                    <p className="text-blue-100">Mobile-responsive on any device</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Simple, Transparent Pricing
            </h2>
            <p className="text-xl text-gray-600">
              Choose the plan that fits your team. All plans include core features.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {dynamicPricing.map((plan, index) => (
              <div 
                key={index}
                className={`bg-white rounded-2xl shadow-lg p-8 ${
                  plan.highlighted 
                    ? 'ring-4 ring-blue-600 relative transform scale-105' 
                    : 'border border-gray-200'
                }`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-blue-600 to-cyan-500 text-white px-4 py-1 rounded-full text-sm font-bold">
                    Most Popular
                  </div>
                )}
                
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{plan.name}</h3>
                  <p className="text-gray-600 mb-4">{plan.description}</p>
                  <div className="flex items-baseline justify-center gap-2">
                    <span className="text-5xl font-bold text-gray-900">{plan.price}</span>
                    {plan.period ? <span className="text-gray-600">/{plan.period}</span> : null}
                  </div>
                  {plan.note && (
                    <p className="text-xs text-gray-500 mt-2">{plan.note}</p>
                  )}
                </div>

                {String(plan.name).toLowerCase() === 'enterprise' || plan.price === 'Contact us' ? (
                  <a href={SALES_MAILTO} className="block mb-6">
                    <Button className="w-full bg-gray-100 hover:bg-gray-200 text-gray-900">Contact us</Button>
                  </a>
                ) : (
                <Button 
                  onClick={() => navigate('/get-started')}
                  className={`w-full mb-6 ${
                    plan.highlighted
                      ? 'bg-gradient-to-r from-blue-600 to-cyan-500 hover:from-blue-700 hover:to-cyan-600 text-white'
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-900'
                  }`}
                >
                  {plan.cta || 'Get Started'}
                </Button>
                )}

                <ul className="space-y-3">
                  {(plan.features || []).map((feature, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Built for a maintenance team
            </h2>
            <p className="text-xl text-gray-600">
              The product does these jobs. It does not publish customer counts or reviews.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              ['Roles', 'Admins, supervisors, and technicians each get the tools their job needs.'],
              ['A shared demo', 'Try Demo Account opens a sample workspace. The records in it are fictional.'],
              ['Checkout price', 'Free is $0 (3 users, 100 work orders total). Pro is $29 per month flat for the company. Enterprise is contact us.'],
            ].map(([title, copy]) => (
              <div key={title} className="bg-gray-50 p-8 rounded-2xl border border-gray-100">
                <h3 className="font-bold text-gray-900 mb-3">{title}</h3>
                <p className="text-gray-700 leading-relaxed">{copy}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about WorkStream Pro
            </p>
          </div>

          <div className="space-y-4">
            {dynamicFaqs.map((faq, index) => (
              <div 
                key={index}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
              >
                <button
                  onClick={() => setOpenFaq(openFaq === index ? null : index)}
                  className="w-full px-6 py-5 text-left flex items-center justify-between hover:bg-gray-50 transition"
                >
                  <span className="font-bold text-gray-900 text-lg">{faq.question}</span>
                  <ChevronDown 
                    className={`w-5 h-5 text-gray-500 transition-transform ${
                      openFaq === index ? 'transform rotate-180' : ''
                    }`}
                  />
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-5 text-gray-600 leading-relaxed">
                    {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-600 to-cyan-500 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6">
            Ready to Transform Your Maintenance?
          </h2>
          <p className="text-xl text-blue-100 mb-10">
            Create a company on the Free plan, or email {SUPPORT_EMAIL} about Enterprise.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              onClick={() => navigate('/get-started')}
              size="lg"
              className="bg-white text-blue-600 hover:bg-gray-100 text-lg px-8 py-6 rounded-xl shadow-lg"
            >
              Get Started Free
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
            <a href={SALES_MAILTO}>
              <Button 
                size="lg"
                variant="outline"
                className="border-2 border-white text-white hover:bg-white/10 text-lg px-8 py-6 rounded-xl"
              >
                <MessageSquare className="mr-2 w-5 h-5" />
                Contact Sales
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-12 mb-12">
            <div>
              <div className="text-2xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent mb-4">
                WorkStream Pro
              </div>
              <p className="text-gray-400 leading-relaxed">
                The modern maintenance management platform built for teams that move fast.
              </p>
            </div>
            
            <div>
              <h4 className="font-bold text-white mb-4">Product</h4>
              <ul className="space-y-2">
                <li><a href="#features" className="hover:text-white transition">Features</a></li>
                <li><a href="#pricing" className="hover:text-white transition">Pricing</a></li>
                <li><a href="/login" className="hover:text-white transition">Try Demo</a></li>
                <li><a href="#features" className="hover:text-white transition">Integrations</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold text-white mb-4">Company</h4>
              <ul className="space-y-2">
                <li><a href="/get-started" className="hover:text-white transition">Start Free Trial</a></li>
                <li><a href="#faq" className="hover:text-white transition">FAQ</a></li>
                <li><a href={SALES_MAILTO} className="hover:text-white transition">Contact Sales</a></li>
                <li><a href={FREE_PM_CHECKLISTS_URL} className="hover:text-white transition">Free PM checklists</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-bold text-white mb-4">Support</h4>
              <ul className="space-y-2">
                <li><a href="#faq" className="hover:text-white transition">Help Center</a></li>
                <li><a href="#features" className="hover:text-white transition">Product Guide</a></li>
                <li><a href="/get-started" className="hover:text-white transition">Getting Started</a></li>
                <li><a href="/login" className="hover:text-white transition">Sign In</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              © 2026 WorkStream Pro. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm">
              <Link to="/privacy" className="hover:text-white transition">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-white transition">Terms of Service</Link>
              <Link to="/cookies" className="hover:text-white transition">Cookie Policy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
};

export default LandingPage;
